// MARK://dhk8/pos/lib/clients/cfdi
// Typed client for dhk8-fiscal-cfdi (CFDI 4.0 stamping / cancellation status).
// Chain: DocketHub → Omnifiscal → FiscalAPI → SAT. NEVER talk to SAT directly.
//
// STATUS: `dhk8-fiscal-cfdi` is planned (PORT-FROM-SC-RESTAURANT §3 cluster
// `fiscal`). The stamp/cancel/status endpoints aren't wired yet.
//
// TODO backend: `dhk8-fiscal-cfdi`. Endpoints needed:
//   POST /v1/cfdi/invoices/:invoiceId/stamp     (idempotent — Idempotency-Key)
//   GET  /v1/cfdi/invoices/:invoiceId           → CfdiStatusDto (uuid, xml, pdf url, sat status)
//   POST /v1/cfdi/invoices/:invoiceId/cancel    { motivo, replacementUuid? }
//   GET  /v1/cfdi/invoices/:invoiceId/cancel-status
// Until then, dev returns a deterministic "stamped" status when `VITE_BILLING_STUB=1`.

import { apiFetch, svcUrl } from '../api.js'

// 2026-05-26 (Lane #178) — antes `${apiOrigin()}/v1/cfdi` → 404 nginx.
// dhk8-fiscal-cfdi expone `/v1/cfdi/*` y se accede vía `/v1/dhk8-fiscal-cfdi/v1/cfdi/*`.
const BASE = svcUrl('dhk8-fiscal-cfdi', '/v1/cfdi')

const STUB_ENABLED = ((): boolean => {
  try {
    return (import.meta.env.VITE_BILLING_STUB ?? '1') !== '0'
  } catch {
    return true
  }
})()

// ── DTOs ───────────────────────────────────────────────────────────────────

export type CfdiSatStatus = 'NOT_STAMPED' | 'STAMPED' | 'CANCELLATION_PENDING' | 'CANCELLED' | 'ERROR'

export interface CfdiStatusDto {
  invoiceId: string
  uuid: string | null
  satStatus: CfdiSatStatus
  stampedAt: string | null
  cancelledAt: string | null
  /** Verification QR target URL (SAT) — for the printed representation. */
  verifyUrl: string | null
  xmlUrl: string | null
  pdfUrl: string | null
  /** Last PAC / chain error message, if any. */
  errorMessage: string | null
}

// ── API ────────────────────────────────────────────────────────────────────

type Envelope<T> = { ok: true; data: T } | { ok: false; error: { code: string; message: string } }
async function fetchData<T>(url: string, init?: Parameters<typeof apiFetch>[1]): Promise<T> {
  const body = await apiFetch<Envelope<T> | T>(url, init)
  if (body !== null && typeof body === 'object' && 'ok' in body) {
    const env = body as Envelope<T>
    if (env.ok) return env.data
    throw new Error(`${env.error.code}: ${env.error.message}`)
  }
  return body as T
}

function stubStatus(invoiceId: string, uuid: string | null, cancelled: boolean): CfdiStatusDto {
  const now = new Date().toISOString()
  return {
    invoiceId,
    uuid,
    satStatus: !uuid ? 'NOT_STAMPED' : cancelled ? 'CANCELLED' : 'STAMPED',
    stampedAt: uuid ? now : null,
    cancelledAt: cancelled ? now : null,
    verifyUrl: uuid
      ? `https://verificacfdi.facturaelectronica.sat.gob.mx/default.aspx?id=${uuid}`
      : null,
    xmlUrl: uuid ? `https://files.dockethub.app/cfdi/${invoiceId}.xml` : null,
    pdfUrl: uuid ? `https://files.dockethub.app/cfdi/${invoiceId}.pdf` : null,
    errorMessage: null,
  }
}

export async function getCfdiStatus(
  invoiceId: string,
  hints?: { uuid?: string | null; cancelled?: boolean },
  signal?: AbortSignal,
): Promise<CfdiStatusDto> {
  if (STUB_ENABLED) return stubStatus(invoiceId, hints?.uuid ?? null, hints?.cancelled ?? false)
  return fetchData<CfdiStatusDto>(`${BASE}/invoices/${invoiceId}`, { signal })
}

export async function stampCfdi(invoiceId: string): Promise<CfdiStatusDto> {
  if (STUB_ENABLED) {
    const uuid = `STUB-${invoiceId.slice(-6).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`
    return stubStatus(invoiceId, uuid, false)
  }
  return fetchData<CfdiStatusDto>(`${BASE}/invoices/${invoiceId}/stamp`, {
    method: 'POST',
    headers: { 'Idempotency-Key': `stamp-${invoiceId}-${Date.now()}` },
  })
}

export async function cancelCfdi(
  invoiceId: string,
  motivo: '01' | '02' | '03' | '04',
  replacementUuid?: string,
): Promise<CfdiStatusDto> {
  if (STUB_ENABLED) return { ...stubStatus(invoiceId, `STUB-${invoiceId.slice(-6)}`, true) }
  return fetchData<CfdiStatusDto>(`${BASE}/invoices/${invoiceId}/cancel`, {
    method: 'POST',
    body: { motivo, replacementUuid },
  })
}
